// seedProducts.js
// Populates Firestore 'products' collection.
// Each product belongs to one of four categories (Gift Items · Toys · Games · Handicrafts)
// and carries a `tags` array of occasion keywords for occasion-based filtering.

import { collection, addDoc, getDocs, query, where, serverTimestamp } from "firebase/firestore";
import { db } from "./config";

export const SAMPLE_PRODUCTS = [

  // ── GIFT ITEMS ──────────────────────────────────────────────────────────────

  {
    name: "Luxury Aromatherapy Candle Gift Set",
    category: "Gift Items",
    tags: ["birthday", "anniversary", "thankyou", "wedding"],
    price: 34.99,
    description:
      "A curated set of four hand-poured soy wax candles — lavender, cedarwood, fresh linen, and warm vanilla. " +
      "Each 200 g candle burns for up to 45 hours. Presented in a matte-black gift box with a satin ribbon. " +
      "Free of paraffin, phthalates, and synthetic dyes. Ideal birthday, anniversary, or housewarming gift.",
    image: "https://images.unsplash.com/photo-1602874801006-8b5f5abe5ece?w=600&q=80",
    stock: 48,
    rating: 4.8,
    reviews: 124,
  },
  {
    name: "Premium Spa & Self-Care Hamper",
    category: "Gift Items",
    tags: ["birthday", "anniversary", "thankyou"],
    price: 89.99,
    description:
      "Everything needed for a five-star spa experience at home. Includes Himalayan bath salts (500 g), " +
      "a rose-clay face mask, bergamot body oil, a bamboo bath tray, a loofah, and two herbal-infusion teas. " +
      "Packed in a reusable rattan basket with shredded paper fill. Suitable for all skin types.",
    image: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=600&q=80",
    stock: 22,
    rating: 4.9,
    reviews: 87,
  },
  {
    name: "Personalized Crystal Photo Frame",
    category: "Gift Items",
    tags: ["birthday", "anniversary", "wedding", "thankyou"],
    price: 42.99,
    description:
      "A high-quality bevelled crystal photo frame with a 5×7 inch display area. " +
      "Includes free laser engraving of a name, date, or short message on the base. " +
      "Comes with a velvet pouch and a gift-ready kraft box. Perfect for anniversaries, weddings, or milestone birthdays.",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&q=80",
    stock: 35,
    rating: 4.7,
    reviews: 203,
  },
  {
    name: "Handcrafted Leather Journal",
    category: "Gift Items",
    tags: ["birthday", "thankyou", "wedding"],
    price: 38.99,
    description:
      "Full-grain vegetable-tanned leather journal with 192 pages of cream-coloured acid-free paper. " +
      "Secured with a wrap-around leather cord. Dimensions: 5.5 × 8.5 inches. " +
      "Each journal is hand-stitched by artisans in Florence — no two covers are identical. " +
      "Available with optional gold-foil monogram stamping at checkout.",
    image: "https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=600&q=80",
    stock: 40,
    rating: 4.8,
    reviews: 156,
  },
  {
    name: "Gourmet Chocolate & Truffle Hamper",
    category: "Gift Items",
    tags: ["birthday", "anniversary", "thankyou", "festival"],
    price: 54.99,
    description:
      "An indulgent hamper featuring 24 Belgian dark, milk, and white chocolate truffles, " +
      "two artisan chocolate bars (sea-salt caramel and hazelnut praline), a hot chocolate sachet, " +
      "and a hand-written gift card. Presented in a magnetic-close keepsake box. " +
      "Gluten-free. Shelf life: 6 weeks from dispatch.",
    image: "https://images.unsplash.com/photo-1548907040-4baa42d10919?w=600&q=80",
    stock: 60,
    rating: 4.9,
    reviews: 318,
  },
  {
    name: "Birthday Celebration Gift Box",
    category: "Gift Items",
    tags: ["birthday"],
    price: 24.99,
    description:
      "A cheerful birthday gift box packed with treats: a mini confetti cake kit, a scented bath bomb, " +
      "a fun patterned notebook, two birthday-themed socks, and a box of artisan shortbread. " +
      "Wrapped in signature tissue paper with a personalised 'Happy Birthday' banner. " +
      "Ready to ship — no wrapping required. Suitable for ages 8 and up.",
    image: "https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?w=600&q=80",
    stock: 55,
    rating: 4.7,
    reviews: 241,
  },
  {
    name: "Wedding Keepsake Memory Book",
    category: "Gift Items",
    tags: ["wedding", "anniversary"],
    price: 67.99,
    description:
      "A luxurious linen-bound keepsake album with 60 archival-quality photo pages, " +
      "a pocket sleeve for mementoes, and beautifully illustrated dividers for ceremony, reception, and honeymoon. " +
      "Gold-foil embossed cover reads 'Our Story'. Dimensions: 12 × 12 inches. " +
      "Each book ships in a white gift box with a personalised name-and-date sticker.",
    image: "https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=600&q=80",
    stock: 20,
    rating: 4.9,
    reviews: 93,
  },
  {
    name: "Thank You Flower & Herbal Tea Gift Set",
    category: "Gift Items",
    tags: ["thankyou", "birthday", "anniversary"],
    price: 29.99,
    description:
      "A heartfelt thank-you gift featuring a hand-tied dried flower arrangement (lavender, pampas, and eucalyptus), " +
      "four single-estate herbal teas (chamomile, peppermint, rose, and jasmine), " +
      "and a 'Thank You' greeting card with a gold envelope. " +
      "Ships in a reusable cotton tote bag. Long-lasting — dried florals stay beautiful for months.",
    image: "https://images.unsplash.com/photo-1487530811015-780d0929f45b?w=600&q=80",
    stock: 45,
    rating: 4.8,
    reviews: 172,
  },

  // ── TOYS ────────────────────────────────────────────────────────────────────

  {
    name: "Melissa & Doug Wooden Building Blocks Set",
    category: "Toys",
    tags: ["kids", "birthday"],
    price: 27.99,
    description:
      "60-piece solid hardwood block set in 9 classic shapes and 4 vibrant colours. " +
      "Smooth-sanded edges ensure safe play. Encourages spatial reasoning, fine motor skills, and creative building. " +
      "Stored in a sturdy wooden crate for easy tidy-up. ASTM safety certified. Recommended ages 2–6.",
    image: "https://images.unsplash.com/photo-1587654780291-39c9404d746b?w=600&q=80",
    stock: 65,
    rating: 4.9,
    reviews: 213,
  },
  {
    name: "STEM Robotics & Coding Kit",
    category: "Toys",
    tags: ["kids", "birthday"],
    price: 94.99,
    description:
      "Build five different robot models using 185 interlocking parts, then bring them to life with the " +
      "drag-and-drop coding app (iOS & Android). Teaches loops, conditionals, and sensor logic through play. " +
      "Includes infrared sensor, servo motor, USB rechargeable battery pack, and illustrated instruction booklet. " +
      "Toy of the Year finalist 2024. Ages 8–14.",
    image: "https://images.unsplash.com/photo-1535378917042-10a22c95931a?w=600&q=80",
    stock: 30,
    rating: 4.8,
    reviews: 96,
  },
  {
    name: "LEGO Creator 3-in-1 Classic Set",
    category: "Toys",
    tags: ["kids", "birthday", "festival"],
    price: 39.99,
    description:
      "Build a classic red race car, a powerful muscle car, or a rugged off-road truck — all from one set! " +
      "505 colourful LEGO bricks with clear step-by-step instructions for all three models. " +
      "Compatible with all LEGO sets. Develops creativity, fine motor skills, and spatial awareness. " +
      "Recommended ages 7–12. An evergreen gift loved by kids worldwide.",
    image: "https://images.unsplash.com/photo-1558060370-d644485927b4?w=600&q=80",
    stock: 50,
    rating: 4.8,
    reviews: 387,
  },
  {
    name: "Giant Unicorn Plush Soft Toy",
    category: "Toys",
    tags: ["kids", "birthday"],
    price: 22.99,
    description:
      "Incredibly soft and cuddly 60 cm unicorn plush with a pastel rainbow mane, golden horn, " +
      "and embroidered rosy cheeks. Made from hypoallergenic plush fabric with PP-cotton stuffing. " +
      "Machine washable. Passes EN 71 and ASTM safety standards. " +
      "Comes in a beautiful gift-ready window box. The perfect companion for children aged 2 and above.",
    image: "https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?w=600&q=80",
    stock: 70,
    rating: 4.7,
    reviews: 529,
  },
  {
    name: "Kids Art & Craft Super Kit",
    category: "Toys",
    tags: ["kids", "birthday", "festival"],
    price: 32.99,
    description:
      "175-piece all-in-one craft set for young artists: 24 watercolour paints, 12 coloured pencils, " +
      "8 oil pastels, washable markers, canvas boards, stencils, foam stickers, and a mini easel. " +
      "Non-toxic, ASTM-certified. Stored in a portable carry case with a handle. " +
      "Perfect for home, school, or travel. Encourages imagination, focus, and self-expression. Ages 4+.",
    image: "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=600&q=80",
    stock: 42,
    rating: 4.8,
    reviews: 264,
  },

  // ── GAMES ───────────────────────────────────────────────────────────────────

  {
    name: "Catan – The Board Game (Base Set)",
    category: "Games",
    tags: ["games", "birthday", "festival"],
    price: 44.99,
    description:
      "The world's best-selling strategy board game. Settle the island of Catan by collecting and trading " +
      "resources — wood, brick, wheat, ore, and sheep — to build roads, settlements, and cities. " +
      "Each game is unique thanks to the modular hex board. For 3–4 players, 60–120 min play time. Ages 10+. " +
      "Expansions sold separately. Over 32 million copies sold worldwide.",
    image: "https://images.unsplash.com/photo-1610890716171-6b1bb98ffd09?w=600&q=80",
    stock: 40,
    rating: 4.8,
    reviews: 341,
  },
  {
    name: "Deluxe Walnut Chess & Backgammon Set",
    category: "Games",
    tags: ["games", "birthday", "anniversary"],
    price: 64.99,
    description:
      "Two timeless games in one elegant set. The folding board is crafted from solid walnut with an " +
      "inlaid playing surface; the weighted Staunton chess pieces are tournament-grade. " +
      "The reverse side features a full backgammon layout with leather-cup dice shakers and doubling cube. " +
      "Folds to a slim 15×15×2 inch carry case with magnetic closure. A perfect gift for strategy-game lovers.",
    image: "https://images.unsplash.com/photo-1529699211952-734e80c4d42b?w=600&q=80",
    stock: 25,
    rating: 4.9,
    reviews: 158,
  },
  {
    name: "Ticket to Ride: Europe Edition",
    category: "Games",
    tags: ["games", "birthday", "festival"],
    price: 54.99,
    description:
      "Claim railway routes across Europe and complete destination tickets in this award-winning board game. " +
      "Features long train routes, tunnels, ferries, and train stations for an extra layer of strategy. " +
      "For 2–5 players, 30–90 minutes. Ages 8+. " +
      "Includes 240 coloured train carriages, 46 destination tickets, and a high-quality map board. " +
      "Winner of multiple international Game of the Year awards.",
    image: "https://images.unsplash.com/photo-1611996575749-79a3a250f948?w=600&q=80",
    stock: 35,
    rating: 4.8,
    reviews: 227,
  },
  {
    name: "Exploding Kittens Card Game",
    category: "Games",
    tags: ["games", "kids", "birthday", "festival"],
    price: 19.99,
    description:
      "The wildly popular card game that combines cats, explosions, and lots of luck. " +
      "Avoid drawing the Exploding Kitten card by using action cards to skip, attack, or defuse. " +
      "For 2–5 players, 15 min average play time. Ages 7+. " +
      "Compact box holds all 56 cards — perfect for game nights, travel, or as a fun stocking stuffer. " +
      "The most-funded Kickstarter card game ever.",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80",
    stock: 80,
    rating: 4.7,
    reviews: 693,
  },
  {
    name: "Codenames – Award-Winning Word Game",
    category: "Games",
    tags: ["games", "birthday", "festival"],
    price: 22.99,
    description:
      "Two rival spymasters give one-word clues to help their team guess the right words on a 5×5 grid " +
      "without hitting the assassin. Fast, tense, and endlessly replayable. " +
      "For 4–8+ players (works with 2 players too), 15–30 min play time. Ages 14+. " +
      "Winner of the 2016 Spiel des Jahres (Game of the Year). " +
      "Perfect for family gatherings, team-building events, or game nights.",
    image: "https://images.unsplash.com/photo-1570303345338-e1f0eddf4946?w=600&q=80",
    stock: 55,
    rating: 4.8,
    reviews: 412,
  },

  // ── HANDICRAFTS ─────────────────────────────────────────────────────────────

  {
    name: "Handmade Ceramic Pour-Over Coffee Set",
    category: "Handicrafts",
    tags: ["birthday", "thankyou", "wedding", "anniversary"],
    price: 52.99,
    description:
      "Artisan wheel-thrown stoneware pour-over set comprising a dripper, a 600 ml carafe, and two tasting cups. " +
      "Finished in a matte slate-blue reactive glaze — no two pieces are identical. " +
      "High-fired at 1280 °C for durability; oven, microwave, and dishwasher safe. " +
      "Each piece is signed by the potter and ships in a gift-ready kraft box. Made in small batches in Portugal.",
    image: "https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?w=600&q=80",
    stock: 18,
    rating: 4.9,
    reviews: 74,
  },
  {
    name: "Boho Macramé Wall Hanging",
    category: "Handicrafts",
    tags: ["wedding", "birthday", "thankyou"],
    price: 46.99,
    description:
      "Large hand-knotted wall hanging crafted from 3 mm natural cotton rope on a reclaimed driftwood dowel. " +
      "Features an intricate diamond-and-fringe pattern measuring 20 inches wide × 38 inches long. " +
      "Each piece is made to order by independent artisans — slight variations add to its one-of-a-kind character. " +
      "Includes a jute hanging cord. Pairs beautifully with bohemian, Scandinavian, or minimalist interiors.",
    image: "https://images.unsplash.com/photo-1614036417651-efe5912149d8?w=600&q=80",
    stock: 14,
    rating: 4.8,
    reviews: 59,
  },
  {
    name: "Hand-Painted Terracotta Planter Set",
    category: "Handicrafts",
    tags: ["festival", "birthday", "thankyou"],
    price: 36.99,
    description:
      "A set of three hand-painted terracotta pots in graduating sizes (4, 6, and 8 inches). " +
      "Each pot features a unique hand-painted Madhubani folk-art motif using non-toxic acrylics. " +
      "Includes drainage holes and matching saucers. Food-safe glaze on interior. " +
      "Handmade by artisan cooperatives in Rajasthan, India. Perfect for succulents, herbs, or gifting.",
    image: "https://images.unsplash.com/photo-1485955900006-10f4d324d411?w=600&q=80",
    stock: 28,
    rating: 4.7,
    reviews: 112,
  },
  {
    name: "Festival LED Lantern Décor Set",
    category: "Handicrafts",
    tags: ["festival", "birthday", "wedding"],
    price: 28.99,
    description:
      "A set of five decorative LED paper lanterns in assorted sizes and warm festival colours. " +
      "Each lantern uses warm-white fairy lights (batteries included) for a soft, glowing ambience. " +
      "Handmade from recycled rice paper with bamboo frames. Foldable for easy storage. " +
      "Perfect for Diwali, weddings, garden parties, and home décor. " +
      "Each set ships flat-packed with an assembly guide taking under two minutes.",
    image: "https://images.unsplash.com/photo-1513519245088-0e12902e35ca?w=600&q=80",
    stock: 38,
    rating: 4.6,
    reviews: 189,
  },
  {
    name: "Handwoven Bamboo Gift Basket",
    category: "Handicrafts",
    tags: ["festival", "thankyou", "wedding", "anniversary"],
    price: 44.99,
    description:
      "A large, naturally handwoven bamboo basket measuring 14 × 10 × 6 inches — beautifully crafted " +
      "by artisans using sustainable mountain bamboo. Features a sturdy double-wrapped handle and " +
      "a removable cotton liner. Ideal as a reusable gift hamper, pantry organiser, or home accent piece. " +
      "Fair-trade certified. Each basket comes with a hangtag telling the story of its maker.",
    image: "https://images.unsplash.com/photo-1605883705077-8d3d3cebe78c?w=600&q=80",
    stock: 24,
    rating: 4.8,
    reviews: 83,
  },
];

// Seeds the Firestore 'products' collection with SAMPLE_PRODUCTS.
// Skips products that already exist (matched by name) to prevent duplicates.
export async function seedProducts() {
  const productsRef = collection(db, "products");
  let added = 0;

  for (const product of SAMPLE_PRODUCTS) {
    const q = query(productsRef, where("name", "==", product.name));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      await addDoc(productsRef, {
        ...product,
        createdAt: serverTimestamp(),
      });
      added++;
    }
  }

  console.log(`Seeding complete. ${added} products added.`);
  return added;
}
